import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, money } from "@/lib/api";
import { Card, Overline } from "@/components/common";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  Sparkles, Sparkle, ArrowRight, Check, ChevronDown, X,
  Search as SearchIcon, TrendingUp, TriangleAlert, Wrench, Users, Package,
} from "lucide-react";

const CATS = ["All", "Critical", "Revenue", "Operations", "Customer", "Inventory"];

const CAT_META = {
  Critical: { icon: TriangleAlert, color: "#DC2626", tint: "bg-[#DC2626]/[0.04] border-[#DC2626]/20" },
  Operations: { icon: Wrench, color: "#2563EB", tint: "bg-[#2563EB]/[0.04] border-[#2563EB]/15" },
  Revenue: { icon: TrendingUp, color: "#0F3F2D", tint: "bg-[#0F3F2D]/[0.04] border-[#0F3F2D]/15" },
  Customer: { icon: Users, color: "#B08423", tint: "bg-[#C7A24A]/[0.06] border-[#C7A24A]/25" },
  Inventory: { icon: Package, color: "#52525B", tint: "bg-[#F3F4F6] border-[#E5E7EB]" },
};

const STATUS_COLOR = {
  Identified: "#71717A", Reviewed: "#2563EB", Approved: "#0F3F2D", "In Progress": "#C7A24A",
  Completed: "#059669", Measured: "#059669", Dismissed: "#A1A1AA",
};

function StatusChip({ status }) {
  const c = STATUS_COLOR[status] || "#71717A";
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full border text-xs font-semibold"
      style={{ color: c, borderColor: `${c}33`, background: `${c}0d` }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: c }} />{status}
    </span>
  );
}

function PipelineStat({ label, value, sub, accent, testid }) {
  return (
    <Card className="p-5" data-testid={testid}>
      <Overline>{label}</Overline>
      <div className={cn("font-display text-2xl font-bold font-mono2 mt-2", accent ? "text-[#0F3F2D]" : "text-[#1C1F1E]")}>{money(value)}</div>
      <div className="text-xs text-[#A1A1AA] mt-0.5">{sub}</div>
    </Card>
  );
}

export default function Insights() {
  const [data, setData] = useState({ rows: [], pipeline: {}, statuses: [] });
  const [cat, setCat] = useState("All");
  const nav = useNavigate();

  const load = () => api.get("/insights").then(({ data }) => setData(data));
  useEffect(() => { load(); }, []);

  const advance = async (row) => {
    const order = data.statuses;
    const idx = order.indexOf(row.status);
    const next = order[Math.min(idx + 1, order.indexOf("Measured"))];
    if (!next || next === row.status) return;
    const { data: res } = await api.post(`/insights/${row.id}/status`, { status: next });
    toast.success(`Marked ${next}`, { description: row.title });
    load();
  };
  const dismiss = async (row) => {
    await api.post(`/insights/${row.id}/status`, { status: "Dismissed" });
    toast("Dismissed", { description: row.title });
    load();
  };
  const goto = (row) => {
    const routes = { "Dispatch Marshal": "/pace", "View Customers": "/customers", "View Inventory": "/pro-shop", "View F&B": "/fnb", "Review Pricing": "/revenue" };
    const r = routes[row.button];
    if (r) nav(r); else toast.success(row.button);
  };

  const filtered = cat === "All" ? data.rows : data.rows.filter((r) => r.category === cat);
  const p = data.pipeline || {};

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tight text-[#1C1F1E] flex items-center gap-2"><Sparkles size={24} className="text-[#C7A24A]" />Insights</h1>
        <p className="text-[#52525B] mt-1">The ScratchHQ intelligence engine — detect, quantify, recommend, act, measure.</p>
      </div>

      {/* Opportunity Pipeline */}
      <div>
        <Overline className="mb-3 block">Scratch Opportunity Pipeline</Overline>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <PipelineStat testid="pipeline-identified" label="Identified" value={p.identified || 0} sub="all detected opportunity" />
          <PipelineStat testid="pipeline-approved" label="Approved" value={p.approved || 0} sub="greenlit by management" />
          <PipelineStat testid="pipeline-captured" label="Captured" value={p.captured || 0} sub="completed or measured" />
          <PipelineStat testid="pipeline-verified" label="Verified Impact" value={p.verified || 0} sub="measured, proven ROI" accent />
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {CATS.map((c) => (
          <button key={c} data-testid={`insight-cat-${c.toLowerCase()}`} onClick={() => setCat(c)}
            className={cn("px-3.5 py-1.5 rounded-full text-sm font-medium border transition-colors",
              cat === c ? "bg-[#0F3F2D] text-white border-transparent" : "bg-white text-[#52525B] border-[#E5E7EB] hover:border-[#0F3F2D]/30")}>
            {c}
          </button>
        ))}
      </div>

      <div className="space-y-4">
        {filtered.map((r) => {
          const meta = CAT_META[r.category] || CAT_META.Revenue;
          const Icon = meta.icon;
          const dismissed = r.status === "Dismissed";
          const measured = r.status === "Measured";
          return (
            <Card key={r.id} className={cn("p-6 border", meta.tint, dismissed && "opacity-60")} data-testid={`insight-${r.priority}`}>
              <div className="flex items-start gap-4">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 border" style={{ borderColor: `${meta.color}33`, background: `${meta.color}0d` }}>
                  <Icon size={17} style={{ color: meta.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <Sparkle size={12} className="text-[#C7A24A]" />
                    <span className="font-display font-semibold text-[#1C1F1E]">{r.title}</span>
                    <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full border ml-1" style={{ color: meta.color, borderColor: `${meta.color}33`, background: `${meta.color}0d` }}>{r.category}</span>
                    <span className="ml-auto flex items-center gap-2">
                      <span className="text-xs text-[#A1A1AA]">{r.department}</span>
                      <StatusChip status={r.status} />
                    </span>
                  </div>

                  <div className="grid md:grid-cols-2 gap-x-8 gap-y-3 mt-3">
                    <div><Overline>What Scratch Detected</Overline><p className="text-sm text-[#1C1F1E] mt-1 font-medium leading-snug">{r.problem}</p></div>
                    <div><Overline>Supporting Evidence</Overline><p className="text-sm text-[#52525B] mt-1 leading-snug">{r.data}</p></div>
                    <div><Overline>Recommended Action</Overline><p className="text-sm text-[#1C1F1E] mt-1 font-medium">{r.action}</p></div>
                    <div><Overline>{measured ? "Verified Impact" : "Estimated Impact"}</Overline><p className="text-sm font-semibold mt-1" style={{ color: meta.color }}>{r.impact}</p></div>
                  </div>

                  <div className="mt-4 flex items-center gap-2 flex-wrap">
                    <button data-testid={`insight-${r.priority}-goto`} onClick={() => goto(r)}
                      className="group inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#0F3F2D] hover:bg-[#165c42] text-white text-sm font-medium transition-colors active:scale-95">
                      {r.button}<ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
                    </button>
                    {!dismissed && !measured && (
                      <button data-testid={`insight-${r.priority}-advance`} onClick={() => advance(r)}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[#E5E7EB] text-sm font-medium text-[#0F3F2D] hover:border-[#0F3F2D]/40 transition-colors">
                        <Check size={14} />Advance Status
                      </button>
                    )}
                    {!dismissed && (
                      <button data-testid={`insight-${r.priority}-dismiss`} onClick={() => dismiss(r)}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[#E5E7EB] text-sm text-[#71717A] hover:border-[#DC2626]/30 hover:text-[#DC2626] transition-colors">
                        <X size={14} />Dismiss
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          );
        })}
        {filtered.length === 0 && <div className="text-center py-16 text-[#A1A1AA]">No insights in this category.</div>}
      </div>
    </div>
  );
}
